import React from "react";
import Button from "@source/components/UI/buttons/button/button";
import FileInput from "@source/components/UI/inputs/fileInput/fileInput";
import Svg from "@source/components/UI/svg/svg";
import iconPictureFileInput from "@source/assets/icons/iconPictureFileInput.svg";
import iconCross from "@source/assets/icons/iconCross.svg";
import {ConsentStepsValues, formatConsentFileSize} from "@source/api/models/consentHelpers";
import {
  ConsentCategorySource,
  ConsentSupportingDocument,
  ConsentTemplate,
  ConsentTemplateInput,
} from "@source/api/models/consentModels";
import {ConsentDocumentPreview, ConsentRequestState} from "@source/components/consentDocuments";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import {PersistStage, persistStageText, readableFileSize} from "./consentStepsShared";
import {StepFooter} from "./stepFooter";
import {TemplateInput} from "./templateInput";
import styles from "./consentSteps.module.scss";

export const DocumentStep = ({
  categoryName,
  source,
  template,
  templateError,
  previewDocument,
  supportingDocuments,
  inputs,
  values,
  inputErrors,
  draftFileName,
  showSavedPdfPreview,
  hasConfirmedPdf,
  selectedPdf,
  draftId,
  persistStage,
  formError,
  needsSigners,
  stale,
  onSelectPdf,
  validatePdfFiles,
  onInputChange,
  onRetryTemplate,
  onCancel,
  onContinue,
  onSave,
  onSend,
  onRefresh,
}: {
  categoryName?: string;
  source?: ConsentCategorySource;
  template: ConsentTemplate | null;
  templateError: string;
  previewDocument: string;
  supportingDocuments: ConsentSupportingDocument[];
  inputs: ConsentTemplateInput[];
  values: ConsentStepsValues;
  inputErrors: Record<string, string>;
  draftFileName?: string;
  showSavedPdfPreview: boolean;
  hasConfirmedPdf: boolean;
  selectedPdf: File | null;
  draftId?: string;
  persistStage: PersistStage;
  formError: string;
  needsSigners: boolean;
  stale: boolean;
  onSelectPdf: (file: File | null) => void;
  validatePdfFiles: (files: File[]) => string;
  onInputChange: (id: string, value: unknown) => void;
  onRetryTemplate: () => void;
  onCancel: () => void;
  onContinue: () => void;
  onSave: () => void;
  onSend: () => void;
  onRefresh: () => void;
}) => {
  const disabled = Boolean(persistStage);
  const isPdf = source === ConsentCategorySource.RAW_PDF;
  const waitingForTemplate = source === ConsentCategorySource.TEMPLATE && !template && !templateError;
  return <section className={styles.detailsStep}>
    <div className={styles.stepBody}>
      <div className={styles.summary}>
        <strong>{categoryName}</strong>
        <span>{source === ConsentCategorySource.TEMPLATE ? "Template document" : "Raw PDF document"}</span>
      </div>
      {isPdf ? <section className={styles.rawState}>
        <strong>Source PDF</strong>
        {hasConfirmedPdf
          ? <span><strong>{draftFileName}</strong> is attached to this draft. You can replace the attachment before sending.</span>
          : <span>Attach one PDF only · maximum 25 MiB.</span>}
        {selectedPdf ? <div className={styles.selectedPdf}>
          <span>{selectedPdf.name} · {readableFileSize(selectedPdf.size)}</span>
          <button type="button" className={styles.clearReplacement} onClick={() => onSelectPdf(null)} disabled={disabled} aria-label="Clear replacement" title="Clear replacement">
            <Svg src={iconCross} color="#7E808F" width={14} height={14}/>
          </button>
        </div> : <div className={styles.updateFileUnputMultiple}><div className={styles.photo}>
          <FileInput
            areaTextClassName={styles.areaText}
            subAreaTextClassName={styles.subAreaText}
            className={styles.img}
            btnClassName={styles.imgBtn}
            selectedFile={null}
            setSelectedFile={onSelectPdf}
            loading={persistStage === "uploading" || persistStage === "confirming"}
            disabled={disabled}
            acceptFormats="application/pdf,.pdf"
            validateFiles={validatePdfFiles}
            uploadImg={iconPictureFileInput}
            imgColor="#7E808F"
            imgWidth={40}
            imgHeight={40}
            areaText="Drop a PDF here, or"
            subAreaText="PDF only · maximum 25 MiB"
          />
        </div></div>}
      </section> : null}
      {isPdf && showSavedPdfPreview && draftId ? <section className={styles.previewSource}>
        <h3>Document preview</h3>
        <p>This shows the saved source currently attached to this draft. Local file selections are not previewed until saved and confirmed.</p>
        <ConsentDocumentPreview documentId={draftId} autoLoad={false} loadLabel="Preview current source"/>
      </section> : null}
      {template ? <section className={styles.previewSource}>
        <h3>Document preview</h3>
        <p>Updates as you complete the fields below.</p>
        <iframe className={styles.previewFrame} sandbox="" srcDoc={previewDocument} title="Consent document preview"/>
      </section> : null}
      {waitingForTemplate ? <ConsentRequestState variant="loading" title="Loading template fields"/> : null}
      {templateError ? <ConsentRequestState variant="error" title="Unable to load template fields" description={templateError} onRetry={onRetryTemplate}/> : null}
      <section className={styles.section}>
        <h3>Supporting information</h3>
        {supportingDocuments.length ? <ul className={styles.supportingList}>
          {supportingDocuments.map((file) => <li className={styles.supportingRow} key={file._id}>
            <div className={styles.supportingMeta}>
              <strong>{file.originalFileName}</strong>
              <span>{formatConsentFileSize(file.size)} · Uploaded {formatConsentDate(file.uploadedAt)}</span>
            </div>
            {file.url ? <Button size="small" variant="outlined" onClick={() => window.open(file.url, "_blank", "noopener,noreferrer")}>View</Button> : null}
          </li>)}
        </ul> : <p>No supporting information for this Consent Category.</p>}
      </section>
      {inputs.length ? <section className={styles.section}>
        <h3>Complete fields below before sending consent</h3>
        {inputs.map((input) => <TemplateInput
          key={input.id}
          input={input}
          value={values.employeeInputValues[input.id]}
          error={inputErrors[input.id]}
          disabled={disabled}
          onChange={(value) => onInputChange(input.id, value)}
        />)}
      </section> : null}
      {!needsSigners && stale ? <Button variant="outlined" onClick={onRefresh} disabled={disabled}>Refresh document</Button> : null}
    </div>
    <StepFooter
      error={formError}
      stageLabel={!needsSigners && persistStage ? `${persistStageText[persistStage]}…` : undefined}
      disabled={disabled}
      onCancel={onCancel}
      onContinue={needsSigners ? onContinue : undefined}
      continueDisabled={source === ConsentCategorySource.TEMPLATE && !template}
      onSave={onSave}
      onSend={onSend}
      saving={persistStage === "saving"}
      sending={persistStage === "sending"}
    />
  </section>;
};
